import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/** Never background-download more than this many variant images. */
const MAX_PRELOAD = 12;

/**
 * @typedef {object} VariantEntry
 * @property {string[]} o - Option values, by option position.
 * @property {number} m - Featured media id.
 * @property {boolean} a - Availability.
 * @property {string} [u] - Featured media image URL (at the theme's max width).
 * @property {number} [r] - Aspect ratio.
 * @property {string} [t] - Alt text.
 *
 * @typedef {object} VariantMediaMap
 * @property {string} id - Product id.
 * @property {VariantEntry[]} variants
 */

/**
 * A custom element that renders a media gallery.
 *
 * PERFORMANCE NOTE (variant image switching)
 * ------------------------------------------
 * Previously this component only reacted to `variant:update`, which is dispatched
 * *after* the variant picker has finished a Section Rendering API `fetch()`. It
 * then called `this.replaceWith(newMediaGallery)`, throwing away every `<img>` in
 * the gallery and re-creating it. That put two serial costs in front of the
 * shopper on every swatch click:
 *
 *   1. a network round trip before anything on screen could change, and
 *   2. a fresh download + decode of the new variant image, because that image had
 *      never been requested by the browser before.
 *
 * We now do the work up front instead:
 *
 *   1. every variant image is downloaded and decoded in the background during
 *      idle time after page load (and immediately on swatch hover), using the
 *      exact same `srcset`/`sizes` the gallery uses, so the browser has the right
 *      candidate in cache before the shopper clicks,
 *   2. `variant:selected` — which fires synchronously on click — swaps the image
 *      from a locally rendered variant → media map, with no network at all, and
 *   3. the `replaceWith()` is skipped whenever the incoming HTML ends up with the
 *      same media set we already have, so there is no DOM teardown, no re-decode
 *      and no flash.
 *
 * The Section Rendering request still runs in the background; price, availability
 * and the buy buttons update from it exactly as before. Only the image no longer
 * waits for it.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    // Fires the moment a swatch is clicked, before any network request.
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener('pointerover', this.#handlePointerOver, { signal, passive: true });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Liquid renders the selected variant's media first, so this is the slot the
    // variant image occupies in the gallery.
    this.#variantSlotId = this.#firstMediaId();

    this.#scheduleWarmUp();
  }

  #controller = new AbortController();

  /** @type {boolean} */
  #warmed = false;

  /** @type {string | null} */
  #variantSlotId = null;

  /** @type {Set<string>} */
  #preloaded = new Set();

  /** @type {VariantMediaMap | null | undefined} */
  #variantMediaMap;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /* -------------------------------------------------------------------------
   * Local variant data
   * ---------------------------------------------------------------------- */

  /**
   * Reads the locally rendered variant → media map. No network, and no parsing
   * cost after the first read. The map is emitted once per product page by the
   * variant picker block, inside the same section as this gallery.
   *
   * @returns {VariantMediaMap | null}
   */
  #getVariantMediaMap() {
    if (this.#variantMediaMap !== undefined) return this.#variantMediaMap ?? null;

    const scope = this.closest('.shopify-section, dialog') ?? document;
    const script = scope.querySelector('script[data-variant-media-map]');

    try {
      this.#variantMediaMap = script?.textContent ? JSON.parse(script.textContent) : null;
    } catch {
      this.#variantMediaMap = null;
    }

    return this.#variantMediaMap ?? null;
  }

  /**
   * The media id of the first media in the gallery.
   * @returns {string | null}
   */
  #firstMediaId() {
    const first = this.querySelector('slideshow-slide .product-media[data-media-id], .product-media[data-media-id]');

    return /** @type {HTMLElement | null} */ (first)?.dataset.mediaId ?? null;
  }

  /**
   * Rewrites a Shopify CDN image URL to a different width.
   *
   * @param {string} url - The base URL.
   * @param {number} width - The width to request.
   * @returns {string} The rewritten URL.
   */
  #urlAt(url, width) {
    return url.replace(/([?&]width=)\d+/, `$1${width}`);
  }

  /**
   * Finds the variant entry matching a picker's current selection.
   *
   * Matching is done per option *position* rather than by variant id, so it stays
   * correct when a picker only exposes a subset of the product's options (which
   * is the case here — "Size" is a combined listing on a separate product).
   *
   * @param {HTMLElement} picker - The variant picker.
   * @param {{ position: number, value: string }} [override] - A hypothetical selection (used on hover).
   * @returns {VariantEntry | null} The matching variant, or null.
   */
  #resolveVariant(picker, override) {
    const data = this.#getVariantMediaMap();
    if (!data?.variants?.length) return null;

    /** @type {Map<number, string>} Option position (1-based) → selected value. */
    const selection = new Map();

    for (const control of picker.querySelectorAll('fieldset input:checked, select')) {
      const node = control instanceof HTMLSelectElement ? control.options[control.selectedIndex] : control;
      const inputId = /** @type {HTMLElement | undefined} */ (node)?.dataset?.inputId;
      if (!inputId) continue;

      const position = Number.parseInt(inputId, 10);
      if (Number.isNaN(position)) continue;

      selection.set(position, /** @type {HTMLInputElement | HTMLSelectElement} */ (control).value);
    }

    if (override) selection.set(override.position, override.value);
    if (!selection.size) return null;

    /** @type {VariantEntry | null} */
    let fallback = null;

    for (const variant of data.variants) {
      let matches = true;

      for (const [position, value] of selection) {
        if (variant.o[position - 1] !== value) {
          matches = false;
          break;
        }
      }

      if (!matches) continue;
      if (variant.a) return variant;
      if (!fallback) fallback = variant;
    }

    return fallback;
  }

  /**
   * Resolves the picker and a hypothetical selection from an option element.
   *
   * @param {Element} element - An input or option element inside a picker.
   * @returns {{ picker: HTMLElement, override?: { position: number, value: string } } | null}
   */
  #context(element) {
    const picker = element.closest('variant-picker, swatches-variant-picker-component');
    if (!(picker instanceof HTMLElement)) return null;

    // Ignore pickers belonging to product cards or quick add dialogs in this section.
    if (picker.closest('product-card, quick-add-dialog')) return null;

    // Ignore pickers for a different product (e.g. a recommendation block).
    const productId = this.#getVariantMediaMap()?.id;
    if (productId && picker.dataset.productId && picker.dataset.productId !== productId) return null;

    const inputId = /** @type {HTMLElement} */ (element).dataset?.inputId;
    const value = /** @type {HTMLInputElement} */ (element).value;
    const position = inputId ? Number.parseInt(inputId, 10) : NaN;

    return {
      picker,
      override: !Number.isNaN(position) && value ? { position, value } : undefined,
    };
  }

  /* -------------------------------------------------------------------------
   * Preloading
   * ---------------------------------------------------------------------- */

  /**
   * Queues the warm-up for after the page has loaded and the main thread is idle,
   * so it never competes with the LCP image or first paint.
   */
  #scheduleWarmUp() {
    const start = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => this.#warmUp(), { timeout: 3000 });
      } else {
        setTimeout(() => this.#warmUp(), 500);
      }
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true, signal: this.#controller.signal });
    }

    // Backstop. The gallery can be re-created mid-load (the theme re-renders the
    // section when a variant is auto-corrected on first paint), which would
    // otherwise strand the scheduled warm-up on a detached instance.
    setTimeout(() => this.#warmUp(), 2000);
  }

  /**
   * Whether background downloading is appropriate on this connection.
   * @returns {boolean}
   */
  #canPreload() {
    const connection = /** @type {any} */ (navigator).connection;

    if (connection?.saveData) return false;
    if (/(^|-)[23]g$/.test(connection?.effectiveType ?? '')) return false;

    return true;
  }

  /**
   * The `sizes`/`srcset` shape of the gallery's main image, so preloaded images
   * resolve to the exact same CDN candidate the gallery will ask for.
   *
   * @returns {{ sizes: string, widths: number[] } | null}
   */
  #imageProfile() {
    const image = this.querySelector('slideshow-slide img.product-media__image, img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return null;

    const widths = [...(image.getAttribute('srcset') ?? '').matchAll(/(\d+)w/g)].map((match) => Number(match[1]));
    if (!widths.length) return null;

    return { sizes: image.getAttribute('sizes') ?? '100vw', widths };
  }

  /**
   * Downloads a single variant image into the browser cache in the background.
   *
   * We deliberately do NOT call `decode()` on bulk preloads: decoding a dozen
   * full-resolution images would hold tens of megabytes of bitmaps and can jank
   * the main thread. Getting the bytes into the HTTP cache removes the part of
   * the delay that actually hurt (the network), and the single image the shopper
   * is about to click is decoded on hover instead.
   *
   * @param {VariantEntry} variant - The variant whose media to preload.
   * @param {boolean} [urgent] - Hover: prioritise it and decode it too.
   * @returns {Promise<void>}
   */
  async #preload(variant, urgent = false) {
    if (!variant?.u) return;
    if (this.#preloaded.has(variant.u)) return;
    if (!urgent && !this.#canPreload()) return;

    const profile = this.#imageProfile();
    if (!profile) return;

    this.#preloaded.add(variant.u);

    const url = variant.u;
    const image = new Image();
    image.decoding = 'async';
    /** @type {any} */ (image).fetchPriority = urgent ? 'high' : 'low';
    image.sizes = profile.sizes;
    // Same candidates the gallery uses, so the browser caches the exact file it
    // will need — no double download.
    image.srcset = profile.widths.map((width) => `${this.#urlAt(url, width)} ${width}w`).join(', ');
    image.src = this.#urlAt(url, profile.widths[profile.widths.length - 1] ?? 3840);

    if (urgent) {
      image.decode?.().catch(() => {});
      return;
    }

    // Settle on load, error, or a timeout — one slow file must never stall the
    // rest of the queue.
    await new Promise((resolve) => {
      const done = () => resolve(undefined);
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
      setTimeout(done, 8000);
    });
  }

  /**
   * Warms the whole gallery: the media already in the DOM, then every variant
   * image that is not in the DOM (which is the case when "Hide unselected
   * variant media" is enabled).
   */
  async #warmUp() {
    // Not `#warmed = true` until the guards pass, so a call that arrives while
    // the element is detached does not permanently disable warming.
    if (this.#warmed || !this.isConnected || !this.#canPreload()) return;
    this.#warmed = true;

    // 1. Media already rendered but lazy. Promote it to `eager` so the browser
    //    fetches the same srcset candidate the slideshow will need. Fire and
    //    forget — `decode()` can stay pending indefinitely for an off-screen
    //    image, which would stall everything behind it.
    //    Zoom-dialog images are skipped: they are full-bleed 100vw assets only
    //    needed once the shopper opens the zoom view.
    for (const image of this.querySelectorAll('img.product-media__image')) {
      if (!(image instanceof HTMLImageElement) || image.closest('zoom-dialog')) continue;
      if (image.loading === 'lazy') image.loading = 'eager';
      image.decode?.().catch(() => {});
    }

    // 2. Variant media that is not rendered at all ("Hide unselected variant
    //    media"). This is the part that actually removes the click delay, so it
    //    is never blocked behind step 1. Sequential, to pace the network.
    const variants = this.#getVariantMediaMap()?.variants ?? [];
    let count = 0;

    for (const variant of variants) {
      if (count >= MAX_PRELOAD) return;
      if (!this.isConnected) {
        // Detached mid-run: let a later trigger pick up where we left off.
        this.#warmed = false;
        return;
      }
      if (!variant.u || this.#preloaded.has(variant.u)) continue;

      count += 1;
      await this.#preload(variant);
    }
  }

  /**
   * Preloads a variant's image as soon as the shopper hovers its swatch, so even
   * the very first click lands on a cached image.
   *
   * @param {PointerEvent} event - The pointerover event.
   */
  #handlePointerOver = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const label = target.closest('.variant-option__button-label, .variant-option__swatch');
    if (!label) return;

    const input = label.querySelector('input');
    if (!input) return;

    const context = this.#context(input);
    if (!context) return;

    const variant = this.#resolveVariant(context.picker, context.override);
    if (variant) this.#preload(variant, true);

    // The shopper is interacting with the picker — make sure the background
    // warm-up is running even if the scheduled one never fired.
    this.#warmUp();
  };

  /* -------------------------------------------------------------------------
   * Instant variant media switching
   * ---------------------------------------------------------------------- */

  /**
   * Handles a variant being selected. This runs synchronously on click, so the
   * image updates in the same frame — long before the section request resolves.
   *
   * @param {Event} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const context = this.#context(target);
    if (!context) return;

    const variant = this.#resolveVariant(context.picker);
    if (variant) this.#showVariantMedia(variant);

    this.#warmUp();
  };

  /**
   * Brings a variant's media on screen.
   *
   * @param {VariantEntry} variant - The selected variant.
   * @returns {boolean} Whether the gallery is now showing the right media.
   */
  #showVariantMedia(variant) {
    if (!variant.m) return false;

    // The media is already rendered (all variant media visible): just move to it.
    if (this.#selectMedia(String(variant.m))) return true;

    // The media is not rendered ("Hide unselected variant media"): swap it into
    // the variant slot in place. No DOM teardown, so no flash and no layout shift
    // — the container's aspect ratio is set by the gallery, not by the image.
    return this.#swapVariantMedia(variant);
  }

  /**
   * Selects an already-rendered media.
   *
   * @param {string} mediaId - The media id.
   * @returns {boolean} Whether a matching media was found.
   */
  #selectMedia(mediaId) {
    const { slideshow } = this;
    const slides = slideshow?.slides;
    let found = false;

    if (slides?.length) {
      const index = slides.findIndex(
        (slide) =>
          /** @type {HTMLElement | null} */ (slide.querySelector('[data-media-id]'))?.dataset.mediaId === mediaId
      );

      if (index >= 0) {
        slideshow?.select(index, undefined, { animate: false });
        found = true;
      }
    }

    // Desktop grid layout: every image is on screen, so nudge the matching one
    // into view. `block: 'nearest'` is a no-op when it is already visible.
    const grid = this.querySelector('.media-gallery__grid');
    if (grid instanceof HTMLElement && grid.offsetParent !== null) {
      const item = grid.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`)?.closest('li');
      if (item) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        found = true;
      }
    }

    return found;
  }

  /**
   * Swaps a new image into the slot the variant media occupies, updating the
   * slide, the zoom dialog entry and its thumbnail.
   *
   * @param {VariantEntry} variant - The selected variant.
   * @returns {boolean} Whether the swap happened.
   */
  #swapVariantMedia(variant) {
    const slotId = this.#variantSlotId;
    if (!slotId || !variant.u) return false;

    // Only swap a slot that genuinely holds a variant image — never a lifestyle
    // shot that happens to be first.
    const variantMediaIds = new Set((this.#getVariantMediaMap()?.variants ?? []).map((entry) => String(entry.m)));
    if (!variantMediaIds.has(slotId)) return false;

    const targets = this.querySelectorAll(`.product-media[data-media-id="${CSS.escape(slotId)}"]`);
    if (!targets.length) return false;

    for (const target of targets) {
      if (!(target instanceof HTMLElement)) continue;

      // Keep the zoom dialog's thumbnail strip in sync with its slide.
      const item = target.closest('.dialog-zoomed-gallery > li');
      if (item?.parentElement) {
        const index = [...item.parentElement.children].indexOf(item);
        const thumbnail = this.querySelectorAll('.dialog-thumbnails-list__thumbnail')[index]?.querySelector('img');
        if (thumbnail instanceof HTMLImageElement) this.#swapImage(thumbnail, variant);
      }

      target.dataset.mediaId = String(variant.m);
      if (variant.r) target.style.setProperty('--ratio', String(variant.r));

      for (const image of target.querySelectorAll('img')) this.#swapImage(image, variant);
    }

    this.#variantSlotId = String(variant.m);
    this.#selectMedia(String(variant.m));

    return true;
  }

  /**
   * Points an existing `<img>` at a new image, preserving whatever width
   * descriptors and `sizes` Liquid generated for it so the browser reuses the
   * candidate we already preloaded.
   *
   * @param {HTMLImageElement} image - The image to repoint.
   * @param {VariantEntry} variant - The variant supplying the new media.
   */
  #swapImage(image, variant) {
    const url = variant.u;
    if (!url) return;

    const srcset = image.getAttribute('srcset');
    if (srcset) {
      const widths = [...srcset.matchAll(/(\d+)w/g)].map((match) => Number(match[1]));
      if (widths.length) {
        image.srcset = widths.map((width) => `${this.#urlAt(url, width)} ${width}w`).join(', ');
      }
    }

    const currentWidth = Number((image.getAttribute('src') ?? '').match(/[?&]width=(\d+)/)?.[1]);
    image.src = this.#urlAt(url, Number.isNaN(currentWidth) ? 3840 : currentWidth);

    if (image.hasAttribute('data_max_resolution')) {
      image.setAttribute('data_max_resolution', this.#urlAt(url, 3840));
    }

    if (typeof variant.t === 'string') image.alt = variant.t;
  }

  /* -------------------------------------------------------------------------
   * Variant update (background section render)
   * ---------------------------------------------------------------------- */

  /**
   * Handles a variant update event.
   *
   * When the incoming gallery holds the same media set we already have — which is
   * now the case for every ordinary variant change, because we have already
   * swapped the variant image in — we keep the existing DOM. Replacing it would
   * re-create and re-decode every image for no visual benefit.
   *
   * We still fall back to a full replacement when the media set genuinely differs
   * (combined listings, or a swap we could not resolve locally), so behaviour is
   * never worse than before.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (this.#hasSameMedia(newMediaGallery)) {
      const mediaId = /** @type {any} */ (event.detail.resource)?.featured_media?.id;
      if (mediaId) this.#selectMedia(String(mediaId));
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Collects the media ids rendered inside a gallery.
   *
   * @param {ParentNode} root - The gallery to read from.
   * @returns {Set<string>} The media ids.
   */
  #mediaIds(root) {
    /** @type {Set<string>} */
    const ids = new Set();

    for (const element of root.querySelectorAll('.product-media[data-media-id]')) {
      const id = /** @type {HTMLElement} */ (element).dataset.mediaId;
      if (id) ids.add(id);
    }

    return ids;
  }

  /**
   * Whether an incoming gallery contains exactly the same media as this one.
   *
   * @param {Element} newMediaGallery - The incoming gallery.
   * @returns {boolean} True when only the order differs.
   */
  #hasSameMedia(newMediaGallery) {
    const current = this.#mediaIds(this);
    const next = this.#mediaIds(newMediaGallery);

    if (!current.size || current.size !== next.size) return false;

    for (const id of next) {
      if (!current.has(id)) return false;
    }

    return true;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
